import { useNavigate } from "react-router-dom";
import { Building2 } from "lucide-react";
import { usePropertyUnreadCount } from "@/hooks/useUnreadCounts";
import { cn } from "@/lib/utils";

interface PropertyCardProps {
  property: {
    id: string;
    name: string;
    address: string | null;
    unit?: string | null;
    property_photo_url: string | null;
    hospitable_property_id?: string | null;
  };
  status?: {
    hasOverdueClean?: boolean;
    hasCleaningToday?: boolean;
    hasActiveTask?: boolean;
    hasOpenIssues?: boolean;
    hasLowInventory?: boolean;
    hasReplenishmentNeeded?: boolean;
  };
}

/** Slice 2 P1/P4 property card — exact Claude Design. */
export const PropertyCard = ({ property, status }: PropertyCardProps) => {
  const navigate = useNavigate();
  const { data: unreadCount = 0 } = usePropertyUnreadCount(property.id);

  const linked = Boolean(String(property.hospitable_property_id || "").trim());
  let badge = linked ? "Linked" : "Manual";
  let badgeClass = linked ? "ch2-badge-teal" : "ch2-badge-grey";
  let next = "—";

  if (status?.hasOverdueClean) {
    next = "Overdue";
  } else if (status?.hasCleaningToday || status?.hasActiveTask) {
    badge = "Today";
    badgeClass = "ch2-badge-teal";
    next = "Cleaning today";
  } else if (status?.hasOpenIssues) {
    next = "Issues";
  } else if (status?.hasLowInventory || status?.hasReplenishmentNeeded) {
    next = "Low stock";
  } else if (unreadCount > 0) {
    next = `${unreadCount} unread`;
  }

  return (
    <button
      type="button"
      onClick={() => navigate(`/properties/${property.id}`)}
      className="w-full overflow-hidden rounded-[18px] border-[1.5px] border-[#e6edf1] bg-white text-left transition-colors hover:border-[#24a89a]/50"
    >
      <div className="h-[118px] w-full bg-[#e8eef2]">
        {property.property_photo_url ? (
          <img src={property.property_photo_url} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <Building2 className="h-10 w-10 text-[#94a3b8]" />
          </div>
        )}
      </div>
      <div className="flex flex-col gap-2 p-3.5">
        <div className="truncate text-[17px] font-bold text-[#0f1720]">{property.name}</div>
        <div className="truncate text-[13px] font-medium text-[#64748b]">
          {property.address || "No address"}
          {property.unit ? ` · Unit ${property.unit}` : ""}
        </div>
        <div className="flex items-center gap-2">
          <span className={cn(badgeClass)}>{badge}</span>
          <span className="text-[12px] font-medium text-[#64748b]">{next}</span>
        </div>
      </div>
    </button>
  );
};
